import { Injectable } from '@angular/core';
import {
  Database,
  ref,
  set,
  update,
  onValue,
  off,
  DatabaseReference,
} from '@angular/fire/database';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RealtimeBookingService {
  constructor(private db: Database) {}

  /**
   * Get a reference to a booking in RTDB
   */
  getBookingRef(bookingId: string): DatabaseReference {
    return ref(this.db, `bookings/${bookingId}`);
  }

  /**
   * Listen to a specific booking's data in real-time
   */
  watchBooking(bookingId: string): Observable<any> {
    const bookingRef = this.getBookingRef(bookingId);
    const subject = new Subject<any>();

    onValue(
      bookingRef,
      (snapshot) => {
        subject.next(snapshot.val());
      },
      (error) => {
        subject.error(error);
      },
    );

    return subject.asObservable();
  }

  /**
   * Update booking status in RTDB
   */
  async updateStatus(
    bookingId: string,
    status: string,
    extraData: any = {},
    userId?: string,
  ) {
    const bookingRef = this.getBookingRef(bookingId);
    await update(bookingRef, {
      status,
      updatedAt: Date.now(),
      updatedBy: userId || 'system',
      ...extraData,
    });
  }

  /**
   * Update LPO state in RTDB
   */
  async updateLpo(
    bookingId: string,
    lpoData: {
      generated?: boolean;
      date?: any;
      url?: string;
      customerSignature?: string | null;
      customerSignatureBase64?: string | null;
      approved?: boolean;
      rejected?: boolean;
      rejectedAt?: string;
      deliveryCharge?: number;
      discount?: number;
      totalAmount?: number;
      baseAmount?: number;
      approvedByVendor?: boolean;
      awaitingCustomerPriceConsent?: boolean;
      priceAdjustmentRejected?: boolean;
      rejectionReason?: string;
    },
    userId?: string,
  ) {
    const bookingRef = this.getBookingRef(bookingId);
    // Create an update object with deep paths to merge data instead of replacing the whole 'lpo' object
    const updates: any = {};
    if (lpoData.generated !== undefined)
      updates['lpo/generated'] = lpoData.generated;
    if (lpoData.date !== undefined) updates['lpo/date'] = lpoData.date;
    if (lpoData.url !== undefined) updates['lpo/url'] = lpoData.url;
    if (lpoData.customerSignature !== undefined)
      updates['lpo/customerSignature'] = lpoData.customerSignature;
    if (lpoData.customerSignatureBase64 !== undefined)
      updates['lpo/customerSignatureBase64'] = lpoData.customerSignatureBase64;
    if (lpoData.approved !== undefined)
      updates['lpo/approved'] = lpoData.approved;
    if (lpoData.rejected !== undefined)
      updates['lpo/rejected'] = lpoData.rejected;
    if (lpoData.rejectedAt !== undefined)
      updates['lpo/rejectedAt'] = lpoData.rejectedAt;
    if (lpoData.deliveryCharge !== undefined)
      updates['lpo/deliveryCharge'] = lpoData.deliveryCharge;
    if (lpoData.discount !== undefined)
      updates['lpo/discount'] = lpoData.discount;
    if (lpoData.totalAmount !== undefined)
      updates['lpo/totalAmount'] = lpoData.totalAmount;
    if (lpoData.baseAmount !== undefined)
      updates['lpo/baseAmount'] = lpoData.baseAmount;
    if (lpoData.approvedByVendor !== undefined)
      updates['lpo/approvedByVendor'] = lpoData.approvedByVendor;
    if (lpoData.awaitingCustomerPriceConsent !== undefined)
      updates['lpo/awaitingCustomerPriceConsent'] =
        lpoData.awaitingCustomerPriceConsent;
    if (lpoData.priceAdjustmentRejected !== undefined)
      updates['lpo/priceAdjustmentRejected'] = lpoData.priceAdjustmentRejected;
    if (lpoData.rejectionReason !== undefined)
      updates['lpo/rejectionReason'] = lpoData.rejectionReason;

    updates['lpo/updatedAt'] = Date.now();
    updates['lpo/updatedBy'] = userId || 'system';
    updates['updatedAt'] = Date.now();

    await update(bookingRef, updates);
  }

  /**
   * Update Timesheet data in RTDB
   */
  async updateTimesheet(
    bookingId: string,
    timesheetData: any,
    userId?: string,
  ) {
    const bookingRef = this.getBookingRef(bookingId);
    await update(bookingRef, {
      timesheet: {
        ...timesheetData,
        updatedAt: Date.now(),
        updatedBy: userId || 'system',
      },
      updatedAt: Date.now(),
    });
  }

  /**
   * Update Quotation state in RTDB
   */
  async updateQuotation(
    bookingId: string,
    quotationData: {
      generated?: boolean;
      date?: any;
      url?: string;
      vendorSignature?: string | null;
      vendorSignatureBase64?: string | null;
      approved?: boolean;
      rejected?: boolean;
      rejectedAt?: string;
      deliveryCharge?: number;
      discount?: number;
      totalAmount?: number;
      baseAmount?: number;
      rejectionReason?: string;
    },
    userId?: string,
  ) {
    const bookingRef = this.getBookingRef(bookingId);
    const updates: any = {};
    if (quotationData.generated !== undefined)
      updates['quotation/generated'] = quotationData.generated;
    if (quotationData.date !== undefined)
      updates['quotation/date'] = quotationData.date;
    if (quotationData.url !== undefined)
      updates['quotation/url'] = quotationData.url;
    if (quotationData.vendorSignature !== undefined)
      updates['quotation/vendorSignature'] = quotationData.vendorSignature;
    if (quotationData.vendorSignatureBase64 !== undefined)
      updates['quotation/vendorSignatureBase64'] =
        quotationData.vendorSignatureBase64;
    if (quotationData.approved !== undefined)
      updates['quotation/approved'] = quotationData.approved;
    if (quotationData.rejected !== undefined)
      updates['quotation/rejected'] = quotationData.rejected;
    if (quotationData.rejectedAt !== undefined)
      updates['quotation/rejectedAt'] = quotationData.rejectedAt;
    if (quotationData.deliveryCharge !== undefined)
      updates['quotation/deliveryCharge'] = quotationData.deliveryCharge;
    if (quotationData.discount !== undefined)
      updates['quotation/discount'] = quotationData.discount;
    if (quotationData.totalAmount !== undefined)
      updates['quotation/totalAmount'] = quotationData.totalAmount;
    if (quotationData.baseAmount !== undefined)
      updates['quotation/baseAmount'] = quotationData.baseAmount;
    if (quotationData.rejectionReason !== undefined)
      updates['quotation/rejectionReason'] = quotationData.rejectionReason;

    updates['quotation/updatedAt'] = Date.now();
    updates['quotation/updatedBy'] = userId || 'system';
    updates['updatedAt'] = Date.now();

    await update(bookingRef, updates);
  }

  /**
   * Update Invoice data in RTDB
   */
  async updateInvoice(bookingId: string, invoiceData: any, userId?: string) {
    const bookingRef = this.getBookingRef(bookingId);
    await update(bookingRef, {
      invoice: {
        ...invoiceData,
        updatedAt: Date.now(),
        updatedBy: userId || 'system',
      },
      updatedAt: Date.now(),
    });
  }

  /**
   * Unsubscribe from updates (handled by Observable teardown usually,
   * but this is a helper for manual ref management)
   */
  stopWatching(bookingId: string) {
    const bookingRef = this.getBookingRef(bookingId);
    off(bookingRef);
  }
}
